import { useState, useEffect, useCallback, useMemo } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useApi } from '../../api';
import { fmtTime } from './statusUtils';
import { PresenceTag } from './AttendanceTags';
import {
  TableCard, TableSkeleton, TableEmpty, TableError, Pager, FilterBar, TH, TD, ROW,
  StatTile, StatTiles, usePagination, PageSizeSelect, RECORD_PAGE_SIZE, SearchInput,
  CONTROL, FILTER_BTN, FilterField, FilterCount, FilterActions,
  useSort, SortableTH, timeKey, PersonCell, TimeInsideBar,
} from './tableUi';

/** Columns the presence board can be ordered by — name, and the two clock times. */
const PRESENCE_SORTS = {
  name: (r: any) => r.name || '',
  first_in: (r: any) => timeKey(r.first_in_at),
  last_out: (r: any) => timeKey(r.last_out_at),
};

/** UC5 — Present in office today? Came today? When did they leave? */

/** Which slice of the board the summary tiles are scoping the table to. */
type Presence = 'all' | 'in' | 'present' | 'absent';

export interface PresenceBoardProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  onOpenEmployee: (id: string) => void;
}

export default function PresenceBoard({ nodeKey = '', onOpenEmployee }: PresenceBoardProps) {
  const { attendance } = useApi();
  const [rows, setRows] = useState<any[]>([]);
  const [workDate, setWorkDate] = useState<string>('');
  /**
   * Full/half-day thresholds behind the time-inside meter, from the same Payroll Setup the
   * day statuses are classified against. Null until the first response, and on a node whose
   * backend predates the field — <TimeInsideBar> then falls back to an 8h day.
   */
  const [dayMinutes, setDayMinutes] = useState<{ full: number; half: number } | null>(null);
  const [date, setDate] = useState<string>(''); // selected date; '' = today (resolved by backend)
  const [loading, setLoading] = useState(true);

  // Filters. `presence` is driven by the summary tiles; search + department narrow
  // independently of it.
  const [presence, setPresence] = useState<Presence>('all');
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [error, setError] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendance.today(date || undefined);
      setRows(res?.employees || []);
      setWorkDate(res?.work_date || '');
      setDayMinutes(res?.day_minutes || null);
      setError('');
      // Seed the picker with the resolved work date on the first (today) load.
      if (!date && res?.work_date) setDate(res.work_date);
    } catch (e: any) {
      console.error('presence fetch failed', e);
      setRows([]);
      // Recorded rather than swallowed: an empty board and a failed request used to look
      // identical, and "nobody is in today" is a costly thing to get wrong.
      setError(e?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, date, nodeKey]);

  // Auto-refresh only when viewing today; historical dates are static.
  const isToday = !date || date === workDate;
  useEffect(() => {
    fetch();
    if (!isToday) return;
    const t = setInterval(fetch, 20_000);
    return () => clearInterval(t);
  }, [fetch, isToday]);

  const departments = useMemo(
    () => Array.from(new Set(rows.map((r) => r.department).filter(Boolean))).sort() as string[],
    [rows],
  );

  // Everything the text + department filters allow through. The tile counts are taken
  // from THIS set (not the raw rows) so a tile's number always equals the number of rows
  // clicking it produces.
  const scoped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (department && (r.department || '') !== department) return false;
      if (!q) return true;
      return [r.name, r.employee_code, r.designation, r.department]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [rows, search, department]);

  const inCount = scoped.filter((r) => r.is_inside).length;
  const presentCount = scoped.filter((r) => r.status !== 'absent').length;
  const absentCount = scoped.length - presentCount;

  const filtered = useMemo(() => {
    switch (presence) {
      case 'in': return scoped.filter((r) => r.is_inside);
      case 'present': return scoped.filter((r) => r.status !== 'absent');
      case 'absent': return scoped.filter((r) => r.status === 'absent');
      default: return scoped;
    }
  }, [scoped, presence]);

  const { sorted, sort, toggleSort } = useSort(filtered, PRESENCE_SORTS);

  const { pageItems, pagerProps, pageSizeProps, setPage } = usePagination(sorted, RECORD_PAGE_SIZE);

  // Any filter or reorder starts the reader back at the top of the list — after a sort,
  // "page 3" refers to entirely different people.
  useEffect(() => { setPage(1); }, [presence, search, department, date, sort, setPage]);

  // Clicking the active tile clears it, so a tile is a toggle rather than a one-way trip.
  const toggle = (v: Presence) => setPresence((cur) => (cur === v ? 'all' : v));
  const filtersActive = presence !== 'all' || !!search || !!department;
  const clearFilters = () => { setPresence('all'); setSearch(''); setDepartment(''); };

  return (
    <div>
      <StatTiles>
        <StatTile
          label="Employees"
          value={scoped.length}
          tone="neutral"
          hint={scoped.length !== rows.length ? `of ${rows.length} total` : undefined}
          active={presence === 'all'}
          onClick={() => setPresence('all')}
        />
        <StatTile
          label={isToday ? 'In office now' : 'In office'}
          value={inCount}
          tone="success"
          hint={isToday ? undefined : 'at end of day'}
          active={presence === 'in'}
          onClick={() => toggle('in')}
        />
        <StatTile
          label="Present"
          value={presentCount}
          tone="info"
          hint={isToday ? 'came in today' : 'came in'}
          active={presence === 'present'}
          onClick={() => toggle('present')}
        />
        <StatTile
          label="Absent"
          value={absentCount}
          tone="muted"
          hint="no sighting"
          active={presence === 'absent'}
          onClick={() => toggle('absent')}
        />
      </StatTiles>

      <FilterBar>
        <FilterField label="Date">
          <input
            type="date"
            value={date}
            max={workDate || undefined}
            onChange={(e) => setDate(e.target.value)}
            aria-label="Work date"
            className={CONTROL}
          />
        </FilterField>
        {!isToday && (
          <button onClick={() => setDate(workDate)} className={FILTER_BTN}>Today</button>
        )}

        <SearchInput value={search} onChange={setSearch} placeholder="Search name, code, designation, department…" label="Search employees" />

        {/* The default option names the filter, so a separate label would just repeat it. */}
        {departments.length > 0 && (
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            aria-label="Filter by department"
            className={`${CONTROL} max-w-[180px]`}
          >
            <option value="">All departments</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        )}

        {filtersActive && (
          <button onClick={clearFilters} className={FILTER_BTN}>
            <XMarkIcon className="h-3.5 w-3.5" /> Clear
          </button>
        )}

        {/* Only spells out the fraction when something is actually filtered — an unfiltered
            board reading "8 of 8" is a subtraction the reader has to do to learn nothing. */}
        <FilterCount>
          {filtersActive ? `${filtered.length} of ${rows.length}` : `${rows.length}`} employees
        </FilterCount>

        <FilterActions>
          <PageSizeSelect {...pageSizeProps} />
          <button onClick={fetch} className={FILTER_BTN} aria-label="Refresh">
            <ArrowPathIcon className="h-3.5 w-3.5" /> Refresh
          </button>
        </FilterActions>
      </FilterBar>

      <TableCard>
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError message={error} onRetry={fetch}>Couldn&apos;t load the presence board</TableError>
        ) : rows.length === 0 ? (
          <TableEmpty>No employees enrolled yet.</TableEmpty>
        ) : filtered.length === 0 ? (
          <TableEmpty>No employees match these filters.</TableEmpty>
        ) : (
          <>
            {/* `table-fixed`: the widths then come from the header rather than from whichever
                name on THIS page happens to be longest, so paging through the roster doesn't
                shuffle the columns sideways — and it's what makes the name cell truncate.

                Percentage shares summing to 100, not rem: with fixed widths every column but
                the first sits as narrow as its content, so all the slack on a wide screen
                lands in the employee column and leaves the clock times huddled on the right. */}
            <table className="w-full min-w-[52rem] table-fixed text-[13px]">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[14%]" />
                <col className="w-[9%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[23%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <SortableTH label="Employee" columnKey="name" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Status</th>
                  <th className={TH}>In</th>
                  <SortableTH label="First in" columnKey="first_in" sort={sort} onSort={toggleSort} />
                  <SortableTH label="Last out" columnKey="last_out" sort={sort} onSort={toggleSort} />
                  <th className={TH}>Time inside</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((r: any) => (
                  <tr key={r.id} className={`${ROW} cursor-pointer`} onClick={() => onOpenEmployee(r.id)}>
                    <td className={TD}>
                      {/* Code, designation AND department: on a roster of forty the code
                          disambiguates the two Sharmas, and the two org facts are how you find
                          the row you came for. Designation first — an operator looks for the
                          line inspector, then checks which department's line it was. */}
                      <PersonCell
                        name={r.name}
                        code={r.employee_code}
                        detail={[r.designation, r.department].filter(Boolean).join(' · ')}
                      />
                    </td>
                    {/* Present / Absent only — the full/half/partial split is a monthly
                        payroll classification, not a live daily state. */}
                    <td className={TD}><PresenceTag status={r.status} /></td>
                    <td className={TD}>{r.is_inside ? <span className="inline-flex items-center gap-1.5 font-medium text-success"><span className="h-2 w-2 rounded-full bg-success" /> In</span> : <span className="text-muted-foreground">—</span>}</td>
                    <td className={`${TD} tabular-nums`}>{fmtTime(r.first_in_at)}</td>
                    <td className={`${TD} tabular-nums`}>{fmtTime(r.last_out_at)}</td>
                    <td className={TD}>
                      <TimeInsideBar
                        minutes={r.total_inside_minutes}
                        fullDayMinutes={dayMinutes?.full}
                        halfDayMinutes={dayMinutes?.half}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager {...pagerProps} />
          </>
        )}
      </TableCard>
    </div>
  );
}
